import React from "react";

import { faArrowDown, faArrowUp } from "@fortawesome/pro-regular-svg-icons";
import { faEllipsisVertical } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { DropDownMenu } from "@performyard/py-components";

import SelectInput from "../../../../../../components/Inputs/SelectInput";

import { SummaryFunction } from "../../../../schemas/report";

import css from "./ReportTable.module.scss";

interface ColumnSort {
	colId: string;
	sort: "asc" | "desc" | null;
	sortIndex: number;
}

interface ColumnHeaderProps {
	displayName: string;
	selectedColumn: {
		field: string;
		fieldValueType: string;
	};
	columnSort: ColumnSort;
	allSorts: ColumnSort[];
	handleSort: (sorts: ColumnSort[]) => void;
	pinned: "left" | null;
	onPinToggle: (field: string) => void;
	canPin: boolean;
	isSummarizeByColumn: boolean;
	isSummarizeRowsView: boolean;
	summaryFunction?: string;
	onInputChange: (value: string, selectedColumn: any) => void;
	availableSummaryFunctions: SummaryFunction[];
}

const optionsLookup: Record<string, string> = {
	average: "Average",
	count: "Count",
	maximum: "Maximum",
	minimum: "Minimum",
	sum: "Sum",
	year: "Year",
	month: "Month",
	quarter: "Quarter",
	automatic: "Automatic",
	5: "5 bins",
	10: "10 bins",
	25: "25 bins",
};

const ColumnHeader = (props: ColumnHeaderProps) => {
	const {
		displayName,
		selectedColumn,
		columnSort,
		allSorts,
		handleSort,
		pinned,
		onPinToggle,
		canPin,
		isSummarizeByColumn,
		isSummarizeRowsView,
		summaryFunction,
		onInputChange,
		availableSummaryFunctions,
	} = props;

	const sortDirection = columnSort?.sort || null;
	const sortIndex = columnSort?.sortIndex === undefined ? null : columnSort?.sortIndex;

	const getNextSortDirection = () => {
		switch (sortDirection) {
			case null:
				return "asc";
			case "asc":
				return "desc";
			default:
				return null;
		}
	};

	const getSortIndex = (shiftHeld: boolean) => {
		let sort = 0;
		if (shiftHeld && sortIndex !== null) {
			sort = sortIndex;
		} else if (shiftHeld && sortIndex === null) {
			sort = allSorts.length;
		}
		return sort;
	};

	const handleSortChange = (event: React.MouseEvent) => {
		const shiftHeld = event.shiftKey;
		const filteredSorts = allSorts.filter((item: any) => item.colId !== selectedColumn.field);
		handleSort([
			...(shiftHeld ? filteredSorts : []),
			{
				colId: selectedColumn.field,
				sort: getNextSortDirection(),
				sortIndex: getSortIndex(shiftHeld),
			},
		]);
	};

	const handlePinSelect = () => {
		onPinToggle(selectedColumn.field);
	};

	let sortIcon;
	if (sortDirection === "asc") {
		sortIcon = <FontAwesomeIcon icon={faArrowUp} className={css.sortIcon} />;
	} else if (sortDirection === "desc") {
		sortIcon = <FontAwesomeIcon icon={faArrowDown} className={css.sortIcon} />;
	}

	const summaryFunctionOptions = availableSummaryFunctions.map((item) => ({
		value: item,
		label: optionsLookup[item] || item,
	}));

	const menuOptions = [];
	if (pinned === "left" || canPin) {
		menuOptions.push({
			key: "togglePin",
			label: pinned === "left" ? "Unpin Column" : "Pin Column",
		});
	}

	return (
		<div className={css.columnHeader}>
			<div className={css.columnHeaderRow}>
				<div className={css.customHeaderLabel} onClick={handleSortChange}>
					<span className={css.text}>{displayName}</span>
					{sortIndex !== null && allSorts.length > 1 ? (
						<span className={css.sortIndex}>{sortIndex + 1}</span>
					) : null}
					{sortIcon}
				</div>
				{!isSummarizeByColumn && menuOptions.length > 0 && (
					<div className={css.kebabWrapper} onClick={(e) => e.stopPropagation()}>
						<DropDownMenu
							options={menuOptions}
							onSelect={handlePinSelect}
							minWidth={140}
							placement="bottom-end"
						>
							<FontAwesomeIcon icon={faEllipsisVertical} className={css.kebabIcon} />
						</DropDownMenu>
					</div>
				)}
			</div>
			{isSummarizeRowsView && summaryFunction && summaryFunctionOptions.length > 1 ? (
				<div className={css.customHeaderSelector}>
					<SelectInput
						placeholder=""
						value={summaryFunction}
						results={summaryFunctionOptions}
						onChange={(val: any) => onInputChange(val, selectedColumn)}
						menuPortalTarget={document.querySelector("body")}
					/>
				</div>
			) : null}
		</div>
	);
};

export default ColumnHeader;
